import React, { useState, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { motion } from "framer-motion";
import { channelsService, ChannelType } from "../../../../lib/api/channels";
import { useAlertStore } from "../../../../stores/alertStore";
import { useOAuth } from "../../../../hooks/useOAuth";
import { useUserChannelsStore } from "../../../../stores/userChannelsStore";
import { useOnboardingStore, OnboardingStep } from "../../../../stores/onboardingStore";
import { Logo } from "../../../../components/common/Logo";
import { usePlanFeaturesStore } from "../../../../stores/planFeaturesStore";
import { UpgradeModal } from "../../../subscription/UpgradeModal";

interface AddChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (channelId: string, channelName?: string) => void;
}

export const AddChannelModal: React.FC<AddChannelModalProps> = ({
  isOpen,
  onClose,
  onComplete,
}) => {
  const { t } = useTranslation("common");
  // const [isConnecting, setIsConnecting] = useState(false); // Replaced by useOAuth
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [showShopifyInput, setShowShopifyInput] = useState(false);
  const [showWooCommerceInput, setShowWooCommerceInput] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setShowShopifyInput(false);
        setShowWooCommerceInput(false);
      }, 300); // Wait for transition to finish
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const CDN_URL = (((window as any).__DASHBAL_CONFIG__?.cdnUrl) || 'https://cdn.dashbal.com').replace(/\/$/, '');

  const { showAlert } = useAlertStore();

  // Entitlement checking
  const { getFeatureValue } = usePlanFeaturesStore();
  const [upgradeModal, setUpgradeModal] = useState({ isOpen: false, featureKey: '' });
  const { channels } = useUserChannelsStore();

  // Fetch available channel types
  const { data: channelsData, isLoading } = useQuery({
    queryKey: ["channelTypes"],
    queryFn: channelsService.getChannelTypes,
    enabled: isOpen,
  });

  const channelTypes = (channelsData?.data || []).filter(c => c.isActive !== false);

  // Helper function to get fallback icon for channels
  const getChannelFallbackIcon = (channelId: string): string => {
    const fallbackIcons: Record<string, string> = {
      shopify: "🛍️",
      woocommerce: "🟣",
      meta: "📱",
      instagram: "📸",
      facebook: "📘",
      google: "🔍",
      tiktok: "🎵",
    };
    return fallbackIcons[channelId] || "📊";
  };

  // Use the new useOAuth hook
  const { loadChannels } = useUserChannelsStore();

  const { completeStep } = useOnboardingStore();
  const handleOAuthSuccess = (data: any) => {
    console.log(`OAuth completed successfully for ${data.channelId}`);
    setIsSuccess(true);

    // Force reload user channels to include the newly connected channel
    loadChannels(true);

    // Call onboarding completion
    completeStep(OnboardingStep.ADD_DATA);

    const channelId = data.channelId || selectedChannel;

    // Close modal and trigger completion after a short delay to show success state
    setTimeout(() => {
      closePopup();
      onComplete(channelId, data.connectionName);
      // Reset success state after modal is likely closed
      setTimeout(() => setIsSuccess(false), 500);
    }, 1500);
  };

  const handleOAuthError = (data: any) => {
    console.error("OAuth error:", data);
    showAlert({
      message: t("channels.oauthFailed", "Unable to connect to this channel. Please try again."),
      type: "error",
    });
    // We don't close the modal on error, allowing retry
  };

  const { isConnecting, initiateOAuth, closePopup } = useOAuth({
    onSuccess: handleOAuthSuccess,
    onError: handleOAuthError
  });

  const handleChannelClick = async (channel: ChannelType) => {
    // Check plan limits
    const maxChannels = getFeatureValue('max_connected_channels');
    if (typeof maxChannels === 'number' && maxChannels !== -1 && channels.length >= maxChannels) {
      setUpgradeModal({ isOpen: true, featureKey: 'max_connected_channels' });
      return;
    }

    setSelectedChannel(channel.id);

    // Shopify uses App Bridge flow - guide users to install from Shopify Admin
    if (channel.id === "shopify") {
      // Show Shopify installation instructions
      setShowShopifyInput(true);
    } else if (channel.id === "woocommerce") {
      setShowWooCommerceInput(true);
    } else {
      // For other channels, proceed with standard OAuth
      try {
        // Initiate OAuth flow to get the URL
        const response = await channelsService.initiateOAuth(channel.id);
        const { authUrl } = response.data;

        // Use hook to open popup and listen for results
        initiateOAuth(authUrl);

      } catch (error: any) {
        console.error("Error initiating OAuth:", error);
        showAlert({
          message: t("channels.failedToConnect", "Unable to connect to this channel. Please try again."),
          type: "error",
        });
        setSelectedChannel(null);
      }
    }
  };
  // Redirect to Shopify App Store
  const handleShopifyConnect = () => {
    const config = (window as any).__DASHBAL_CONFIG__;
    const appStoreUrl = config?.shopifyAppLink || 'https://apps.shopify.com/dashbal';
    window.open(appStoreUrl, '_blank');
    onClose();
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60]"
          onClose={() => !isConnecting && !showShopifyInput && onClose()}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 modal-backdrop" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="bg-white dark:bg-neutral-900 rounded-xl md:rounded-2xl shadow-2xl max-w-4xl w-full h-[95vh] md:h-auto md:max-h-[90vh] flex flex-col transform transition-all dark:border dark:border-neutral-800">
                  {/* Header */}
                  <div className="px-4 py-4 md:px-6 md:py-5 border-b border-neutral-200 dark:border-neutral-800">
                    <div className="flex items-center justify-between">
                      {showShopifyInput || showWooCommerceInput ? (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => {
                              setShowShopifyInput(false);
                              setShowWooCommerceInput(false);
                            }}
                            className="flex items-center gap-1 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200 transition-colors"
                          >
                            <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_10892b15.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_10892b15.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            {t("channels.backToChannels")}
                          </button>
                        </div>
                      ) : (
                        <div>
                          <h2 className="text-lg md:text-xl font-bold text-neutral-900 dark:text-neutral-100">
                            {t("channels.connectChannel")}
                          </h2>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 md:mt-1">
                            {t("channels.choosePlatform")}
                          </p>
                        </div>
                      )}
                      <button
                        onClick={onClose}
                        disabled={isConnecting}
                        className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg disabled:opacity-50"
                      >
                        <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_725495b4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_725495b4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-y-auto p-3.5 md:p-6 scrollbar-hide">
                    {showWooCommerceInput ? (
                      <div className="w-full max-w-md mx-auto">
                        <div className="bg-transparent md:bg-white dark:md:bg-neutral-900 border-0 md:border border-neutral-200 dark:border-neutral-800 rounded-xl p-0 md:p-5 text-center">
                          <div className="flex flex-col items-center gap-2 mb-4">
                            <div className="w-12 h-12 bg-[#7F54B3]/10 rounded-xl flex items-center justify-center">
                              <img src={`${CDN_URL}/logos/channel_logos/woocommerce/Woo_primary.png`} alt="WooCommerce" className="w-8 h-8 object-contain" />
                            </div>
                            <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                              {t("channels.connectWooCommerce", "Connect WooCommerce Store")}
                            </h3>
                          </div>

                          <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4 mb-5 text-left">
                            <p className="text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed mb-4">
                              {t("channels.wooIntro", "Connect your WooCommerce store by installing our dedicated WordPress plugin. This allows us to securely sync your orders and sales data.")}
                            </p>
                            <ul className="text-xs text-neutral-600 dark:text-neutral-300 space-y-3">
                              <li className="flex items-start gap-2.5">
                                <span className="flex-shrink-0 w-5 h-5 bg-accent-primary/10 text-accent-primary rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5">1</span>
                                <span>{t("channels.wooStep1", "Download the Dashbal Connector plugin using the button below.")}</span>
                              </li>
                              <li className="flex items-start gap-2.5">
                                <span className="flex-shrink-0 w-5 h-5 bg-accent-primary/10 text-accent-primary rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5">2</span>
                                <span>{t("channels.wooStep2", "Go to Plugins > Add New > Upload on your WordPress dashboard.")}</span>
                              </li>
                              <li className="flex items-start gap-2.5">
                                <span className="flex-shrink-0 w-5 h-5 bg-accent-primary/10 text-accent-primary rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5">3</span>
                                <span>{t("channels.wooStep3", "Activate the plugin and click 'Connect to Dashbal' to finalize.")}</span>
                              </li>
                            </ul>
                          </div>

                          <a 
                            href={`${CDN_URL}/downloads/dashbal-connector.zip`}
                            download="dashbal-connector.zip"
                            className="w-full py-3 px-6 bg-accent-primary hover:bg-[#b3562e] text-white font-bold rounded-lg transition-all flex items-center justify-center gap-2 text-sm shadow-md"
                          >
                            <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_3a1a9e9a.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_3a1a9e9a.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            <span>{t("channels.downloadPlugin", "Download Dashbal Plugin")}</span>
                          </a>
                        </div>
                      </div>
                    ) : showShopifyInput ? (
                      <div className="w-full max-w-md mx-auto">
                        <div className="bg-transparent md:bg-white dark:md:bg-neutral-900 border-0 md:border border-neutral-200 dark:border-neutral-800 rounded-xl p-0 md:p-5 text-center">
                          <div className="flex flex-col items-center gap-2 mb-4">
                            <div className="w-12 h-12 bg-[#95BF47]/10 rounded-xl flex items-center justify-center">
                              <img src={`${CDN_URL}/logos/channel_logos/shopify/shopify_primary.png`} alt="Shopify" className="w-8 h-8 object-contain" />
                            </div>
                            <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                              {t("channels.connectShopify", "Connect Shopify Store")}
                            </h3>
                          </div>

                          <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4 mb-5 text-left">
                            <p className="text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed mb-4">
                              {t("channels.shopifyIntro", "Connect your Shopify store by installing our official app. This ensures a secure and automated sync of your orders and analytics.")}
                            </p>
                            <ul className="text-xs text-neutral-600 dark:text-neutral-400 space-y-3">
                              <li className="flex items-start gap-2.5">
                                <span className="flex-shrink-0 w-5 h-5 bg-accent-primary/10 text-accent-primary rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5">1</span>
                                <span>{t("channels.shopifyStep1", "Click 'Install Dashbal App' below to begin.")}</span>
                              </li>
                              <li className="flex items-start gap-2.5">
                                <span className="flex-shrink-0 w-5 h-5 bg-accent-primary/10 text-accent-primary rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5">2</span>
                                <span>{t("channels.shopifyStep2", "Log in to your Shopify store and approve permissions.")}</span>
                              </li>
                              <li className="flex items-start gap-2.5">
                                <span className="flex-shrink-0 w-5 h-5 bg-accent-primary/10 text-accent-primary rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5">3</span>
                                <span>{t("channels.shopifyStep3", "Sit back while we sync your data for the first time!")}</span>
                              </li>
                            </ul>
                          </div>

                          <button
                            onClick={handleShopifyConnect}
                            className="w-full py-3 px-6 bg-accent-primary hover:bg-[#b3562e] text-white font-bold rounded-lg transition-all flex items-center justify-center gap-2 text-sm shadow-md"
                          >
                            <span>{t("channels.installDashbalApp", "Install Dashbal App")}</span>
                            <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_eb6d23d3.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_eb6d23d3.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                          </button>
                        </div>
                      </div>
                    ) : isConnecting ? (
                      <div className="flex flex-col items-center justify-center py-10 md:py-20">
                        <div className="relative w-20 h-20 mb-6">
                          {/* Pulsing background glow */}
                          <motion.div
                            animate={isSuccess ? { scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] } : { scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
                            transition={{ duration: isSuccess ? 1 : 2, repeat: Infinity, ease: 'easeInOut' }}
                            className={`absolute inset-0 ${isSuccess ? 'bg-green-500/20' : 'bg-accent-primary/20'} rounded-full blur-xl`}
                          />

                          {/* Spinning ring or Success check */}
                          {!isSuccess ? (
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                              className="absolute inset-0 m-auto w-20 h-20 rounded-full border-2 border-transparent border-t-accent-primary border-r-accent-primary/30"
                            />
                          ) : (
                            <motion.div
                              key="success"
                              initial={{ scale: 0, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ type: "spring", stiffness: 260, damping: 20 }}
                              className="absolute inset-0 m-auto w-20 h-20 rounded-full border-2 border-green-500 flex items-center justify-center bg-green-50 dark:bg-green-900/20 shadow-lg shadow-green-500/10"
                            >
                              <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <motion.path
                                  initial={{ pathLength: 0 }}
                                  animate={{ pathLength: 1 }}
                                  transition={{ duration: 0.4, delay: 0.2 }}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </motion.div>
                          )}

                          {/* Logo with subtle pulse (only when loading) */}
                          {!isSuccess && (
                            <motion.div
                              animate={{ scale: [1, 1.05, 1] }}
                              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                              className="absolute inset-0 flex items-center justify-center"
                            >
                              <Logo className="w-10 h-10" />
                            </motion.div>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                          {isSuccess 
                            ? t("success.channelConnected")
                            : t("channels.connectingTo", { channel: channelTypes.find(c => c.id === selectedChannel)?.name || selectedChannel })}
                        </h3>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md">
                          {isSuccess 
                            ? t("oauth.authorizationSuccessful")
                            : t("channels.completeAuthorization")}
                        </p>
                      </div>
                    ) : isLoading ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                          <div key={i} className="p-4 md:p-6 border-2 border-neutral-100 dark:border-neutral-800 rounded-xl animate-pulse">
                            <div className="flex md:block items-center md:items-start gap-4 md:gap-0">
                              <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-neutral-100 dark:bg-neutral-800 md:mb-4 flex-shrink-0" />
                              <div className="flex-1 space-y-2">
                                <div className="h-4 bg-neutral-100 dark:bg-neutral-800 rounded w-1/2" />
                                <div className="h-3 bg-neutral-100 dark:bg-neutral-800 rounded w-1/3" />
                                <div className="hidden md:block h-3 bg-neutral-100 dark:bg-neutral-800 rounded w-1/4 pt-2" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : channelTypes.length === 0 ? (
                      <div className="text-center py-8 md:py-16">
                        <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-neutral-100 dark:bg-neutral-800 mb-4">
                          <span className="w-8 h-8 md:w-10 md:h-10 text-neutral-400 dark:text-neutral-500" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_d4ae4183.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_d4ae4183.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </div>
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                          {t("channels.noChannelsAvailable")}
                        </h3>
                        <p className="text-neutral-600 dark:text-neutral-400">
                          {t("channels.contactSupport")}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {channelTypes.map((channel: ChannelType) => {
                            // Normal allowed channel card
                            return (
                                <button
                                  key={channel.id}
                                  onClick={() => handleChannelClick(channel)}
                                  disabled={isConnecting}
                                  className="group relative p-4 md:p-6 border-2 border-neutral-200 dark:border-neutral-800 rounded-xl hover:border-accent-primary dark:hover:border-accent-primary hover:shadow-lg transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-neutral-800/50 overflow-hidden"
                                >
                                  {/* Hover gradient effect */}
                                  <div className="absolute inset-0 bg-gradient-to-br from-[#faf6f5] to-transparent dark:from-neutral-800 dark:to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                                  <div className="relative flex md:block items-center md:items-start gap-4 md:gap-0">
                                    {
                                      /* Icon */
                                    }
                                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-gradient-to-br from-neutral-100 to-neutral-200 dark:from-neutral-700 dark:to-neutral-600 group-hover:from-[#f5e8e3] group-hover:to-[#ead9d3] dark:group-hover:from-neutral-700 dark:group-hover:to-neutral-600 flex items-center justify-center md:mb-4 transition-all group-hover:scale-110 flex-shrink-0">
                                      {(() => {
                                        // Use icon from backend (Single Source of Truth)
                                        if (channel.icon) {
                                          if (channel.icon.startsWith("http") || channel.icon.startsWith("/")) {
                                            return (
                                              <img
                                                src={channel.icon}
                                                alt={channel.name}
                                                className="w-8 h-8 md:w-10 md:h-10 object-contain"
                                                onError={(e) => {
                                                  e.currentTarget.style.display = "none";
                                                  const fallback = document.createElement("span");
                                                  fallback.className = "text-2xl md:text-4xl";
                                                  fallback.textContent = getChannelFallbackIcon(channel.id);
                                                  e.currentTarget.parentNode?.appendChild(fallback);
                                                }}
                                              />
                                            );
                                          } else {
                                            return <span className="text-2xl md:text-4xl">{channel.icon}</span>;
                                          }
                                        }

                                        return (
                                          <span className="w-8 h-8 md:w-10 md:h-10 text-neutral-400 group-hover:text-accent-primary transition-colors" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_3f9e81ca.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_3f9e81ca.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                        );
                                      })()}
                                    </div>

                                    {/* Details */}
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-bold text-neutral-900 dark:text-neutral-100 mb-0.5 md:mb-1 group-hover:text-[#b3562e] transition-colors text-sm md:text-base truncate">
                                        {channel.name}
                                      </h4>
                                      <p className="text-xs md:text-sm text-neutral-500 dark:text-neutral-400 md:mb-3 truncate">
                                        {channel.category}
                                      </p>

                                      {/* Connect button */}
                                      <div className="hidden md:flex items-center gap-2 text-accent-primary group-hover:text-[#b3562e] font-medium text-sm">
                                        <span>{t("channels.connect")}</span>
                                        <span className="w-4 h-4 group-hover:translate-x-1 transition-transform" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_39427f59.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_39427f59.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                      </div>
                                    </div>

                                    {/* Mobile arrow */}
                                    <div className="md:hidden">
                                      <span className="w-5 h-5 text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_39427f59.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_39427f59.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    </div>
                                  </div>
                                </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-4 py-3 md:px-6 md:py-4 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50 rounded-b-xl">
                    <div className="flex justify-between items-center gap-2">
                      <p className="text-xs md:text-sm text-neutral-500 dark:text-neutral-400 truncate">
                        <span className="w-3.5 h-3.5 md:w-4 md:h-4 inline mr-1 text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_8c8c26e6.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_8c8c26e6.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        {t("channels.dataSecure")}
                      </p>
                      <button
                        onClick={onClose}
                        disabled={isConnecting}
                        className="px-3 py-2 md:px-5 md:py-2.5 text-xs md:text-sm text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white font-medium transition-colors hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg disabled:opacity-50"
                      >
                        {isConnecting ? t("channels.connecting") : t("channels.cancel")}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        onClose={() => setUpgradeModal({ isOpen: false, featureKey: '' })}
        featureKey={upgradeModal.featureKey}
        currentUsage={channels.length}
      />
    </>
  );
};
